/**
 * Shared time utilities for Sports pages.
 * Always use fmt12() for any time displayed to customers or owners.
 */

/**
 * Convert HH:MM (24-hour, with optional past-midnight >24) to 12-hour AM/PM string.
 *   "05:00" → "5:00 AM"
 *   "14:30" → "2:30 PM"
 *   "18:00" → "6:00 PM"
 *   "24:00" → "12:00 AM"  (midnight)
 *   "25:30" → "1:30 AM (+1)"  (past midnight next day)
 */
export function fmt12(t) {
  if (!t) return '';
  const parts = t.split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  if (h >= 24) {
    const adjH = h - 24;
    if (adjH === 0) return `12:${String(m).padStart(2, '0')} AM`;
    const ampm = adjH >= 12 ? 'PM' : 'AM';
    const h12 = adjH % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}${h >= 25 ? ' (+1)' : ''}`;
  }
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/**
 * Format a court hours range: "5:00 AM – 12:00 AM"
 */
export function fmtHours(open, close) {
  return `${fmt12(open)} – ${fmt12(close)}`;
}

/**
 * Format a Date object as YYYY-MM-DD using LOCAL date parts (NOT UTC).
 * This avoids the toISOString() bug where dates shift backwards in UTC+N timezones.
 */
export function ymd(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Get today's date string in a specific IANA timezone (e.g. 'Asia/Manila').
 * Falls back to browser-local time if the timezone is invalid.
 */
export function ymdTz(tz, d = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch {
    return ymd(d);
  }
}

/**
 * Add N days to a YYYY-MM-DD string and return YYYY-MM-DD.
 */
export function addDaysYmd(ymdStr, n) {
  const [y, m, dd] = ymdStr.split('-').map(Number);
  const d = new Date(y, m - 1, dd);
  d.setDate(d.getDate() + n);
  return ymd(d);
}
